// @flow
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { List } from 'immutable';


import { getAccounts, refreshAccountsCompleted, refreshAccountsStatuses } from 'data/accounts/actions';
import { getAccountsById } from 'data/accounts/selectors';
import { transformResponseToRefreshStatus } from 'data/accounts/transformers';
import { Account } from 'data/accounts/types';
import {
  addInstitutionLoginAndAccounts as addInstitutionLoginAndAccountsAction,
  addInstitutionLoginAndAccountsResponse,
  getInstitutionLogins,
} from 'data/institutionLogins/actions';
import { getInstitutionLoginForId } from 'data/institutionLogins/selectors';
import { transformMfaResponseToMfaChallenge } from 'data/institutionLogins/transformers';
import type { InstitutionLogin } from 'data/institutionLogins/types';
import { getTransactions } from 'data/transactions/actions';

import AxiosFactory from 'utils/axiosFactory';
import { Config, ENVIRONMENT } from 'utils/webappConfig';
import { getLogger } from 'utils/logger';

import * as actions from './actions';
import * as transformers from './transformers';
import {
  AccountDiscoveryCredentialsFormSubmitError,
  AccountDiscoveryMfaFormSubmitError,
} from './types'

const log = getLogger('containers/UpsertFinancialInstitution/sagas.js');
const qcsAxios = AxiosFactory.get('qcs');

// poll every 3 seconds up to a total of 3 minutes and 30 seconds (seconds are buffer over QCS timeouts)
const REFRESH_POLL_INTERVAL = 3;
const REFRESH_POLL_ATTEMPTS = 210 / REFRESH_POLL_INTERVAL;

export function* accountDiscovery({ payload: institutionLogin, meta }: {payload: InstitutionLogin, meta: any }): Generator<*, *, *> {
  log.log('Account Discovery Started', institutionLogin);
  const { resolve, reject } = meta;

  let resolvedInstitutionLogin = institutionLogin;
  let institutionLoginId = institutionLogin.id !== '0' ? institutionLogin.id : null;

  let requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins`;
  if (institutionLoginId) {
    requestUrl += `/${institutionLoginId}`;
  }
  let requestBody = transformers.transformInstitutionLoginToReqestData(institutionLogin);

  try {
    const httpMethod = institutionLoginId ? qcsAxios.put : qcsAxios.post;
    const response = yield call(httpMethod, requestUrl, requestBody);
    log.log('Upserted institition-login, response=', response.data);

    if (!institutionLoginId) {
      institutionLoginId = response.data.id;
      resolvedInstitutionLogin = resolvedInstitutionLogin.set('id', institutionLoginId);
    }
  } catch (exceptionError) {
    log.log('Received error', exceptionError);
    let error = exceptionError;
    if (error.response) {
      if (!institutionLoginId && error.response.status === 409 && error.response.data.errors[0].extData.attributeName === 'Credentials') {
        log.info('Received 409 response, turning request into update');

        // Server detected that we are creating a login in which we already have one for the same username; so,
        // until server does it, we will redo requesting after changing to an update instead of a create
        try {
          institutionLoginId = error.response.data.errors[0].extData.conflictId;
          const conflictingInstitutionLogin = yield select(getInstitutionLoginForId, institutionLoginId);

          // here we are swapping out institutionLogin to the conflicting institutionLogin
          //
          resolvedInstitutionLogin = conflictingInstitutionLogin.merge({
            credentials: institutionLogin.credentials,
          });

          requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins`;
          requestUrl += `/${institutionLoginId}`;
          requestBody = transformers.transformInstitutionLoginToReqestData(resolvedInstitutionLogin);

          const response = yield call(qcsAxios.put, requestUrl, requestBody);
          log.log('Upserted conflicting institution-login, response=', response.data);

          // clear error since put was sucessful
          error = null;
        } catch (exceptionError2) {
          error = exceptionError2;
        }
      }
    }
    if (error) {
      log.log('Error upserting institution-login, error=', error);
      const errorResponse = new AccountDiscoveryCredentialsFormSubmitError(error);

      yield put(actions.accountDiscoveryCredentialsFormSubmitResponse(errorResponse));
      yield call(reject, resolvedInstitutionLogin);

      // if in error, don't continue
      return;
    }
  }

  yield put(actions.accountDiscoveryCredentialsFormSubmitResponse(resolvedInstitutionLogin));
  yield call(resolve, resolvedInstitutionLogin);
  yield put(getInstitutionLogins());

  // make flow happy
  institutionLoginId = institutionLoginId || '0';

  // poll until institution-login is ready or cancel action
  requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/${institutionLoginId}/poll`;
  yield* poll(requestUrl, institutionLoginId);
}

export function* submitMfaResponses({ payload: mfaResponse, meta }: {payload: any, meta: any}) : Generator<*, *, *> {
  log.log('Submitting MFA Response for Account Discovery', mfaResponse);
  const { resolve, reject } = meta;

  let requestUrl = `${Config.environments[ENVIRONMENT].services_url}${mfaResponse.submitUrl}`;
  const requestBody = {
    institutionLoginId: mfaResponse.institutionLoginId,
    mfaResponses: mfaResponse.answers.map((answer) => ({
      key: answer.id,
      response: answer.value,
    })),
  };

  try {
    yield call(qcsAxios.post, requestUrl, requestBody);

    yield put(actions.accountDiscoveryMfaFormSubmitResponse());
    yield call(resolve, mfaResponse.institutionLoginId);

  } catch (error) {
    log.error('Received error', error);
    const errorResponse = new AccountDiscoveryMfaFormSubmitError(error);

    yield put(actions.accountDiscoveryMfaFormSubmitResponse(errorResponse));
    yield call(reject, errorResponse);

    // if in error, don't continue
    return;
  }

  // poll until institution-login is ready or cancel action
  requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/${mfaResponse.institutionLoginId}/poll`;
  yield* poll(requestUrl, mfaResponse.institutionLoginId);
}

function* poll(url, institutionLoginId) {
  let response;
  let timeout = REFRESH_POLL_ATTEMPTS;
  while (timeout > 0) {
    try {
      response = yield call(qcsAxios.get, url);
      log.log('Polling upserted institition-logins, response=', response.data);

      const adStatus = transformers.transformResponseToAccountDiscoveryStatus(response);
      yield put(actions.accountDiscoveryStatus(adStatus));

      const { data } = response;
      if (data.status === 'ACTION_REQUIRED') {
        const { aggregators } = response.data;
        if (aggregators.length !== 1) {
          throw new Error('Unsupported number of aggregators when extracting MFA Challenge');
        }
        const aggregator = aggregators[0];
        if (aggregator.aggStatus !== 'MFA_CHALLENGE_READY_FOR_USER') {
          throw new Error('Trying to transform response to MFA Challenge when aggStatus != MFA_CHALLENGE_READY_FOR_USER');
        }

        // todo: this should really come from QCS
        const submitUrl = `/institution-logins/${data.institutionLoginId}/mfa-response`;

        const mfaChallenge = transformMfaResponseToMfaChallenge(
          data.institutionLoginId, aggregator.mfaChallenges, submitUrl
        );
        yield put(actions.accountDiscoveryMfaRequired(mfaChallenge));
      }

      if (!data.isProcessing) {
        const institutionLogin = yield select(getInstitutionLoginForId, adStatus.institutionLoginId);

        if (data.status === 'OK') {
          if (data.mode === 'UPDATE_CREDENTIALS') {
            yield put(actions.accountDiscoverySuccess());
          } else {
            const allAccountsById = yield select(getAccountsById);
            const accountDiscoveryData = transformers.transformResponseToAccountDiscoveryData(data, allAccountsById);
            yield put(actions.accountDiscoverySuccessWithAccounts(accountDiscoveryData));
          }
        } else if (data.status !== 'ACTION_REQUIRED') {
          log.log('Status is in another terminated state', data.status);
          yield put(actions.accountDiscoveryFailure({ adStatus, institutionLogin }));
        } else {
          yield put(actions.accountDiscoveryStopped({ adStatus, institutionLogin }));
        }

        // server is done processing so stop polling
        return;
      }

      timeout -= 1;
      yield delay(REFRESH_POLL_INTERVAL * 1000);

    } catch (error) {
      log.log(error);
      const institutionLogin = yield select(getInstitutionLoginForId, institutionLoginId);
      yield put(actions.accountDiscoveryError(error, institutionLogin));
      return;
    }
  }
}

// =================================================================================================
// getPartnerAuthUri
// =================================================================================================
export function* getPartnerAuthUris({ payload }: {payload: any}): Generator<*, *, *> {
  log.info('getPartnerAuthUris called...', payload);

  const { partnerAuth, redirectUri } = payload;
  const { intuitProperty, partnerUid } = partnerAuth;

  const requestUrl =
    `${Config.environments[ENVIRONMENT].services_url}/intuit-partnerauth?intuit_property=${intuitProperty}&partner_uid=${partnerUid}&offering_redirect_uri=${redirectUri}`;

  try {
    const response = yield call(qcsAxios.get, requestUrl);
    log.info('Retrieved partnerAuthUris, response=', response);

    const partnerAuthUris = transformers.transformResponseToPartnerAuthUris(response);
    log.info('getPartnerAuthUris response after transform=', partnerAuthUris);

    yield put(actions.getPartnerAuthUrisSuccess(partnerAuthUris));
  } catch (error) {
    log.info('Error retrieving partnerAuthUris', error);
    yield put(actions.getPartnerAuthUrisFailure(error));
  }
}

function transformAddInstitutionLoginAndAccountsPayloadWithResponse(
  institutionLogin: InstitutionLogin, accounts: List<Account>, response: any
) {
  const updatedInstitutionLogin = institutionLogin.set('id', response.data.id);

  const { correlationIds } = response.data;
  const updatedAccounts = accounts.map((account) => {
    const accountId = correlationIds[account.clientId];
    return account.merge({
      'id': accountId,
      'institutionLoginId': updatedInstitutionLogin.id,
      'isDeleted': account.isDeleted || account.isIgnored,
    });
  });
  return [ updatedInstitutionLogin, updatedAccounts ];
}

// =================================================================================================
// discoverInstitutionLoginWithAccounts
// =================================================================================================
export function* discoverInstitutionLoginWithAccounts({ payload: { institutionLogin, accounts } }: { payload: any }): Generator<*, *, *> {
  log.log('Discovering institition-login with accounts', institutionLogin, accounts);

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/discover`;
  const requestBody = transformers.transformInstitutionLoginToReqestData(institutionLogin, accounts);

  try {
    // const institutionLoginToUpsert = institutionLogin;
    // const accountDiscoveryData = transformers.transformAccountsToDiscoverAccount(institutionLogin, accounts);

    const response = yield call(qcsAxios.post, requestUrl, requestBody);
    log.info('Discovered institition-login with accounts, response=', response.data);

    const institutionLoginToUpsert = response.data.existingInstitutionLoginId
      ? yield select(getInstitutionLoginForId, response.data.existingInstitutionLoginId)
      : institutionLogin;

    const allAccountsById = yield select(getAccountsById);
    const accountDiscoveryData = transformers.transformResponseToAccountDiscoveryData(response.data, allAccountsById);

    log.info('Transformed values', institutionLoginToUpsert, accountDiscoveryData);
    yield put(actions.discoverInstitutionLoginWithAccountsResponse({ institutionLogin: institutionLoginToUpsert, accountDiscoveryData}));
  } catch (error) {
    log.log('Error discovering institution-login with accounts, error=', error);
    yield put(actions.discoverInstitutionLoginWithAccountsResponse(error));
  }
}

// =================================================================================================
// rediscoverInstitutionLogin
// =================================================================================================
export function* rediscoverInstitutionLogin({ payload: institutionLogin }: { payload: any }): Generator<*, *, *> {
  log.log('Rediscovering institition-login', institutionLogin);

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/${institutionLogin.id}/rediscover`;
  const requestBody = transformers.transformInstitutionLoginToReqestData(institutionLogin);

  try {
    const response = yield call(qcsAxios.post, requestUrl, requestBody);
    log.info('Rediscovered institition-login, response=', response.data);

    const allAccountsById = yield select(getAccountsById);
    const accountDiscoveryData = transformers.transformResponseToAccountDiscoveryData(response.data, allAccountsById);

    log.info('Transformed values', accountDiscoveryData);
    yield put(actions.rediscoverInstitutionLoginResponse({institutionLogin, accountDiscoveryData}));
  } catch (error) {
    log.log('Error rediscovering institution-login, error=', error);
    yield put(actions.rediscoverInstitutionLoginResponse(error));
  }
}

// =================================================================================================
// addInstitutionLoginAndAccounts
// =================================================================================================
export function* addInstitutionLoginAndAccounts({ payload: { institutionLogin, accounts } }: { payload: any }): Generator<*, *, *> {
  log.log('Creating InstitutionLogin with accounts', institutionLogin, accounts);

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins`;
  const requestBody = transformers.transformInstitutionLoginToReqestData(institutionLogin, accounts);

  let institutionLoginId = null;
  let pollingUrl = null;

  try {
    const response = yield call(qcsAxios.post, requestUrl, requestBody);
    log.info('Created institition-login and added accounts, response=', response.data);

    const [ updatedInstitutionLogin, updatedAccounts ] =
      transformAddInstitutionLoginAndAccountsPayloadWithResponse(institutionLogin, accounts, response);
    log.info('Transformed valuese', updatedInstitutionLogin, updatedAccounts);

    institutionLoginId = updatedInstitutionLogin.id;
    pollingUrl = `${Config.environments[ENVIRONMENT].services_url}${response.data.pollingReference}`;

    yield put(addInstitutionLoginAndAccountsResponse({ institutionLogin: updatedInstitutionLogin, accounts: updatedAccounts }));
  } catch (error) {
    log.log('Error adding institution-login and accounts, error=', error);
    yield put(addInstitutionLoginAndAccountsResponse(error));

    // if in error, don't continue
    return;
  }

  yield* doAddInstitutionLoginAndAccountsPolling(pollingUrl, institutionLoginId);
}

function* doAddInstitutionLoginAndAccountsPolling(url, institutionLoginId, meta): Generator<*, *, *> {
  let numTimesLeftToPoll = REFRESH_POLL_ATTEMPTS;
  while (numTimesLeftToPoll > 0) {
    try {
      const response = yield call(qcsAxios.get, url);
      log.log('Polling for refresh status', response.data);

      const refreshStatus = transformResponseToRefreshStatus(response.data);
      log.log('Status after transforming...', refreshStatus);

      // send refresh status
      yield put(refreshAccountsStatuses(refreshStatus));

      if (!response.data.isProcessing) {
        yield put(refreshAccountsCompleted({ institutionLoginId, status: 'DONE_PROCESSING' }, meta ));

        // since we are done processing, stop polling
        break;
      }

      numTimesLeftToPoll -= 1;
      log.log(`Number of times left to poll is ${numTimesLeftToPoll}`);
      yield delay(REFRESH_POLL_INTERVAL * 1000);
    } catch (error) {
      log.log('How did we get here...', error);
      yield put(refreshAccountsCompleted({ institutionLoginId, status: 'FAILED_WHEN_POLLING' }, meta));
      break;
    }
  }

  if (numTimesLeftToPoll === 0) {
    yield put(refreshAccountsCompleted({ institutionLoginId, status: 'TIMEOUT_WHILE_PROCESSING' }, meta));
  }

  // go ahead and get updated institution logins and accounts so we have latest that was created
  yield put(getInstitutionLogins());
  yield put(getAccounts());
  yield put(getTransactions(undefined, { context: 'refresh' }));
}

// =================================================================================================
// getPlaidPublicToken
// =================================================================================================
export function* getPlaidPublicToken({ payload: institutionLogin }: {payload: InstitutionLogin }): Generator<*, *, *> {
  log.info('getPlaidPublicToken called...', institutionLogin);

  const requestUrl =
    `${Config.environments[ENVIRONMENT].services_url}//aggregator-plaid/public-token/${institutionLogin.id}`;

  try {
    const response = yield call(qcsAxios.get, requestUrl);
    log.info('Retrieved public token, response=', response);

    const publicToken = transformers.transformResponseToPlaidPublicToken(response);
    log.info('Plaid public token response after transform=', publicToken);

    yield put(actions.getPlaidPublicTokenResponse({ institutionLogin, publicToken }));
  } catch (error) {
    log.info('Error retrieving Plaid public token', error);
    yield put(actions.getPlaidPublicTokenResponse(error));
  }
}

// =================================================================================================
// logPlaidItemEvent
// =================================================================================================
export function* logPlaidItemEvent({ payload: { institutionLogin, event } }: { payload: any }): Generator<*, *, *> {
  log.info('Logging Plaid event', institutionLogin, event);

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/institution-logins/log-user-activity`;
  const requestBody = transformers.transformPlaidItemActivityToReqestData(institutionLogin, event);

  try {
    yield call(qcsAxios.post, requestUrl, requestBody);
    yield put(actions.logPlaidItemEventResponse());
  } catch (error) {
    log.log('Error logging Plaid activity, error=', error);
    yield put(actions.logPlaidItemEventResponse(error));
  }
}

export function* accountDiscoveryCredentialsFormSubmitWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.accountDiscoveryCredentialsFormSubmit, accountDiscovery);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* accountDiscoveryMfaFormWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.accountDiscoveryMfaFormSubmit, submitMfaResponses);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* getPartnerAuthUrisWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.getPartnerAuthUris, getPartnerAuthUris);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* discoverInstitutionLoginWithAccountsWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.discoverInstitutionLoginWithAccounts, discoverInstitutionLoginWithAccounts);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* rediscoverInstitutionLoginWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.rediscoverInstitutionLogin, rediscoverInstitutionLogin);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* addInstitutionLoginsAndAccountsWatcher(): Generator<*, *, *> {
  yield takeLatest(addInstitutionLoginAndAccountsAction, addInstitutionLoginAndAccounts);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* getPlaidPublicTokenWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.getPlaidPublicToken, getPlaidPublicToken);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* logPlaidItemActivityWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.logPlaidItemEvent, logPlaidItemEvent);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

// Load all watcher sagas
export default [
  accountDiscoveryCredentialsFormSubmitWatcher,
  accountDiscoveryMfaFormWatcher,
  getPartnerAuthUrisWatcher,
  discoverInstitutionLoginWithAccountsWatcher,
  rediscoverInstitutionLoginWatcher,
  addInstitutionLoginsAndAccountsWatcher,
  getPlaidPublicTokenWatcher,
  logPlaidItemActivityWatcher,
];
